import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CardActions,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
  Fade,
} from "@mui/material";
import {
  UserPlus,
  ShieldCheck,
  Sparkles,
  Printer,
  Layers,
  BarChart3,
  Search,
  GraduationCap,
  Award,
  BadgeCheck,
  QrCode,
  ArrowUpRight,
  Copy,
  Check,
  KeyRound,
  PowerOff,
  SlidersHorizontal,
  CheckCircle2,
  Lock,
  Radio,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { API_ENDPOINT, APP_ID, ONSITE_MODULE_ITEMS } from "../../utils/constants";
import MuPb from "../../widgets/MuPb";
import OtpInput from "./OtpInput";
import { LoadingButton } from "@mui/lab";
import axios from "axios";
import { toast } from "react-toastify";
import { toastError } from "../../utils/util";
import {
  deleteOnsite,
  deleteOtpVerify,
  getOnsite,
  getOtpVerify,
  isOnsiteActivated,
  saveOnsite,
  saveOtpVerify,
} from "./utils";
import OnsiteReuseContainer from "./OnsiteReuseContainer";

// Metadata mapping for icons, palettes, and badges
const getModuleMeta = (module, index) => {
  const key = (module.key || "").toUpperCase();
  const title = (module.title || "").toLowerCase();

  if (key.includes("REGISTRATION") || title.includes("registration")) {
    return {
      icon: <UserPlus size={24} />,
      color: "#2563EB",
      bgColor: "#EFF6FF",
      borderColor: "#BFDBFE",
      tag: "Registration",
    };
  }
  if (key.includes("SECURITY") || title.includes("security") || title.includes("scan") || title.includes("gate")) {
    return {
      icon: <ShieldCheck size={24} />,
      color: "#7C3AED",
      bgColor: "#F5F3FF",
      borderColor: "#DDD6FE",
      tag: "Access Control",
    };
  }
  if (key.includes("KIOSK") || title.includes("kiosk") || title.includes("self")) {
    return {
      icon: <Sparkles size={24} />,
      color: "#DB2777",
      bgColor: "#FDF2F8",
      borderColor: "#FBCFE8",
      tag: "Self Service",
    };
  }
  if (key.includes("BULK_PRINTING") || title.includes("bulk printing")) {
    return {
      icon: <Layers size={24} />,
      color: "#059669",
      bgColor: "#ECFDF5",
      borderColor: "#A7F3D0",
      tag: "Batch Printing",
    };
  }
  if (key.includes("FOOTFALL") || title.includes("footfall") || title.includes("report") || title.includes("analysis")) {
    return {
      icon: <BarChart3 size={24} />,
      color: "#EA580C",
      bgColor: "#FFF7ED",
      borderColor: "#FED7AA",
      tag: "Live Analytics",
    };
  }
  if (key.includes("SEARCH") || title.includes("search")) {
    return {
      icon: <Search size={24} />,
      color: "#0284C7",
      bgColor: "#F0F9FF",
      borderColor: "#BAE6FD",
      tag: "Quick Search",
    };
  }
  if (key.includes("STUDENT") || title.includes("student")) {
    return {
      icon: <GraduationCap size={24} />,
      color: "#D97706",
      bgColor: "#FFFBEB",
      borderColor: "#FDE68A",
      tag: "Institutions",
    };
  }
  if (key.includes("STAFF") || title.includes("staff")) {
    return {
      icon: <Award size={24} />,
      color: "#9333EA",
      bgColor: "#FAF5FF",
      borderColor: "#E9D5FF",
      tag: "Staff & Team",
    };
  }
  if (key.includes("SERVICE") || title.includes("service")) {
    return {
      icon: <BadgeCheck size={24} />,
      color: "#0D9488",
      bgColor: "#F0FDFA",
      borderColor: "#99F6E4",
      tag: "Services",
    };
  }

  const defaultPalettes = [
    { color: "#2563EB", bgColor: "#EFF6FF", borderColor: "#BFDBFE", tag: "Operations" },
    { color: "#7C3AED", bgColor: "#F5F3FF", borderColor: "#DDD6FE", tag: "Security" },
    { color: "#059669", bgColor: "#ECFDF5", borderColor: "#A7F3D0", tag: "Printing" },
    { color: "#EA580C", bgColor: "#FFF7ED", borderColor: "#FED7AA", tag: "Analytics" },
  ];
  const p = defaultPalettes[index % defaultPalettes.length];
  return {
    icon: <QrCode size={24} />,
    color: p.color,
    bgColor: p.bgColor,
    borderColor: p.borderColor,
    tag: p.tag,
  };
};

const SharableOnsite = () => {
  const { id } = useParams();
  const activeToken = id || APP_ID;

  // States
  const [open, setOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otp, setOtp] = useState("");
  const [currentGroup, setCurrentGroup] = useState();
  const [requesting, setRequesting] = useState(false);

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_ENDPOINT}/onsite/get-modules`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${activeToken}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        const staffModule = {
          key: ONSITE_MODULE_ITEMS.PRINT_STAFF_BADGES,
          title: "Print Staff Badge",
          description: "Import staff list from Excel file and print badges quickly.",
          color: "#9333EA",
          link: "/onsite/print-staff-badge",
        };

        const hasStaff = Array.isArray(data) && data.some(
          (m) =>
            m.key === ONSITE_MODULE_ITEMS.PRINT_STAFF_BADGES ||
            m.key === "PRINT_STAFF_BADGES" ||
            m.key === "PRINT_STAFF_BADGE" ||
            m.link === "/onsite/print-staff-badge"
        );

        const finalModules = Array.isArray(data)
          ? (hasStaff ? data : [...data, staffModule])
          : [staffModule];

        setModules(finalModules);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (activeToken) {
      fetchModules();
    }
  }, [activeToken]);

  const handleClose = () => {
    setOpen(false);
  };

  const onOtpEntered = async (otp) => {
    setOtp(otp);
  };

  const deactivateModule = (group) => {
    deleteOnsite(group.key);
    toast.info(`${group.title} has been deactivated`);
    setTimeout(() => {
      document.location.reload();
    }, 300);
  };

  const requestOtp = async (cd) => {
    try {
      setRequesting(true);
      setCurrentGroup(cd);
      if (!cd._id) {
        saveOnsite(cd.key, "activated");
        toast.success(`${cd.title} activated successfully`);
        setTimeout(() => {
          document.location.reload();
        }, 300);
        return;
      }
      const { data } = await axios.get(
        `${API_ENDPOINT}/onsite/request-activation-code?id=${cd._id}`,
        {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${activeToken}`,
          },
        }
      );
      toast.success(data.message);
      saveOtpVerify(data.token);
      onActivate(cd);
      setOpen(true);
    } catch (error) {
      toastError(error);
    } finally {
      setRequesting(false);
    }
  };

  const onActivate = (cd) => {
    setCurrentGroup(cd);
    setOpen(true);
  };

  const onSubmitOtp = async () => {
    try {
      setVerifying(true);
      const { data } = await axios.get(
        `${API_ENDPOINT}/onsite/verify-activation-code?otp=${otp}&code=${getOtpVerify()}`,
        {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${activeToken}`,
          },
        }
      );
      toast.success(data.message);
      saveOnsite(data.key, data.token);
      setOpen(false);
      deleteOtpVerify();
      setTimeout(() => {
        document.location.reload();
      }, 300);
    } catch (error) {
      toastError(error);
    } finally {
      setVerifying(false);
    }
  };

  const handleCopyLink = (group) => {
    const fullLink = `${window.location.origin}${group.link}`;
    navigator.clipboard.writeText(fullLink);
    setCopiedKey(group.key);
    toast.success("Module link copied to clipboard!");
    setTimeout(() => {
      setCopiedKey(null);
    }, 2000);
  };

  // Filtered modules
  const filteredModules = useMemo(() => {
    return (modules || []).filter((mod) => {
      const matchesSearch =
        (mod.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (mod.description || "").toLowerCase().includes(searchQuery.toLowerCase());

      const isActive = isOnsiteActivated(mod.key);
      if (selectedFilter === "active") return matchesSearch && isActive;
      if (selectedFilter === "inactive") return matchesSearch && !isActive;
      return matchesSearch;
    });
  }, [modules, searchQuery, selectedFilter]);

  const activeCount = useMemo(() => {
    return (modules || []).filter((m) => isOnsiteActivated(m.key)).length;
  }, [modules]);

  if (loading) {
    return <MuPb />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: "12px", my: 4 }}>
        {error || "Oops! could not load data"}
      </Alert>
    );
  }

  return (
    <OnsiteReuseContainer maxWidth="lg">
      {/* Hero Header Section */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 0.6,
            borderRadius: "20px",
            bgcolor: "rgba(37, 99, 235, 0.08)",
            color: "#2563EB",
            fontWeight: 700,
            fontSize: "0.82rem",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          <Sparkles size={14} />
          Onsite Operations Hub
        </Box>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 800,
            color: "#0F172A",
            fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.5rem" },
            letterSpacing: "-0.03em",
            mb: 1,
          }}
        >
          Live Venue & Kiosk Modules
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#64748B",
            maxWidth: "700px",
            mx: "auto",
            fontSize: { xs: "0.95rem", sm: "1.05rem" },
            lineHeight: 1.5,
          }}
        >
          Manage fast visitor check-ins, high-speed badge printing, security scanning checkpoints, and live footfall analytics.
        </Typography>

        {/* Quick Stats Banner & Filters */}
        <Box
          sx={{
            mt: 3.5,
            p: { xs: 2, sm: 2.5 },
            bgcolor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px -1px rgba(15, 23, 42, 0.04), 0 2px 4px -2px rgba(15, 23, 42, 0.02)",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {/* Status Filter Chips */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
            <Chip
              label={`All Modules (${modules.length})`}
              onClick={() => setSelectedFilter("all")}
              color={selectedFilter === "all" ? "primary" : "default"}
              variant={selectedFilter === "all" ? "filled" : "outlined"}
              sx={{ fontWeight: 600, borderRadius: "8px", px: 0.5 }}
            />
            <Chip
              icon={<CheckCircle2 size={16} color={selectedFilter === "active" ? "#FFFFFF" : "#10B981"} />}
              label={`Active (${activeCount})`}
              onClick={() => setSelectedFilter("active")}
              color={selectedFilter === "active" ? "success" : "default"}
              variant={selectedFilter === "active" ? "filled" : "outlined"}
              sx={{ fontWeight: 600, borderRadius: "8px", px: 0.5 }}
            />
            <Chip
              icon={<Lock size={15} color={selectedFilter === "inactive" ? "#FFFFFF" : "#64748B"} />}
              label={`Inactive (${modules.length - activeCount})`}
              onClick={() => setSelectedFilter("inactive")}
              color={selectedFilter === "inactive" ? "secondary" : "default"}
              variant={selectedFilter === "inactive" ? "filled" : "outlined"}
              sx={{ fontWeight: 600, borderRadius: "8px", px: 0.5 }}
            />
          </Stack>

          {/* Search Input */}
          <TextField
            size="small"
            placeholder="Search module by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} color="#94A3B8" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", md: "280px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                bgcolor: "#F8FAFC",
                fontSize: "0.9rem",
              },
            }}
          />
        </Box>
      </Box>

      {/* Grid of Modules */}
      {filteredModules.length === 0 ? (
        <Box
          sx={{
            py: 8,
            textAlign: "center",
            bgcolor: "#FFFFFF",
            borderRadius: "16px",
            border: "1px dashed #CBD5E1",
          }}
        >
          <Search size={40} color="#94A3B8" style={{ marginBottom: "12px" }} />
          <Typography variant="h6" fontWeight={700} color="#0F172A">
            No modules match your search
          </Typography>
          <Typography variant="body2" color="#64748B" sx={{ mt: 0.5, mb: 2 }}>
            Try adjusting your search terms or filters.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setSearchQuery("");
              setSelectedFilter("all");
            }}
          >
            Reset Filters
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredModules.map((group, index) => {
            const meta = getModuleMeta(group, index);
            const isActivated = isOnsiteActivated(group.key);

            return (
              <Grid item xs={12} sm={6} md={4} key={group.key || index}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: "18px",
                    bgcolor: "#FFFFFF",
                    border: isActivated ? `1.5px solid ${meta.color}40` : "1px solid #E2E8F0",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: isActivated
                      ? `0 10px 25px -5px ${meta.color}15, 0 4px 6px -2px ${meta.color}0A`
                      : "0 4px 6px -1px rgba(15, 23, 42, 0.03), 0 2px 4px -2px rgba(15, 23, 42, 0.02)",
                    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: `0 20px 30px -10px ${meta.color}25, 0 8px 12px -4px rgba(15, 23, 42, 0.06)`,
                      borderColor: meta.color,
                    },
                  }}
                >
                  {/* Subtle top accent bar */}
                  <Box
                    sx={{
                      height: "4px",
                      width: "100%",
                      bgcolor: meta.color,
                      opacity: isActivated ? 1 : 0.6,
                    }}
                  />

                  <CardContent sx={{ p: 3, pb: 2 }}>
                    {/* Header: Icon & Category Tag */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          bgcolor: meta.bgColor,
                          color: meta.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: `1px solid ${meta.borderColor}`,
                        }}
                      >
                        {meta.icon}
                      </Box>
                      <Chip
                        label={meta.tag}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          bgcolor: meta.bgColor,
                          color: meta.color,
                          border: `1px solid ${meta.borderColor}`,
                          borderRadius: "6px",
                        }}
                      />
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#0F172A",
                        fontSize: "1.15rem",
                        lineHeight: 1.35,
                        mb: 1,
                      }}
                    >
                      {group.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748B",
                        lineHeight: 1.55,
                        fontSize: "0.88rem",
                        minHeight: "42px",
                      }}
                    >
                      {group.description}
                    </Typography>

                    {/* Activation Status Badge */}
                    <Box sx={{ mt: 2.5 }}>
                      {isActivated ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "8px",
                            bgcolor: "#ECFDF5",
                            border: "1px solid #A7F3D0",
                          }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "#10B981",
                              boxShadow: "0 0 0 3px rgba(16, 185, 129, 0.2)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#065F46",
                              fontWeight: 700,
                              fontSize: "0.78rem",
                            }}
                          >
                            Active & Ready
                          </Typography>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.8,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: "8px",
                            bgcolor: "#F1F5F9",
                            border: "1px solid #E2E8F0",
                          }}
                        >
                          <Lock size={12} color="#64748B" />
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#64748B",
                              fontWeight: 600,
                              fontSize: "0.78rem",
                            }}
                          >
                            Requires Activation
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>

                  {/* Actions Footer */}
                  <CardActions
                    sx={{
                      p: 2.5,
                      pt: 0,
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    {!isActivated ? (
                      <LoadingButton
                        fullWidth
                        loading={currentGroup?._id === group._id && requesting}
                        variant="contained"
                        startIcon={<KeyRound size={16} />}
                        onClick={() => requestOtp(group)}
                        sx={{
                          py: 1,
                          fontWeight: 700,
                          fontSize: "0.88rem",
                          borderRadius: "10px",
                          textTransform: "none",
                          bgcolor: meta.color,
                          boxShadow: `0 4px 12px ${meta.color}35`,
                          "&:hover": {
                            bgcolor: meta.color,
                            opacity: 0.92,
                          },
                        }}
                      >
                        Activate Module
                      </LoadingButton>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 1.5,
                        }}
                      >
                        <Link
                          to={group.link}
                          style={{ textDecoration: "none", flexGrow: 1 }}
                        >
                          <Button
                            fullWidth
                            variant="contained"
                            endIcon={<ArrowUpRight size={16} />}
                            sx={{
                              py: 1,
                              fontWeight: 700,
                              fontSize: "0.88rem",
                              borderRadius: "10px",
                              textTransform: "none",
                              bgcolor: meta.color,
                              boxShadow: `0 4px 12px ${meta.color}35`,
                              "&:hover": {
                                bgcolor: meta.color,
                                opacity: 0.92,
                              },
                            }}
                          >
                            Open
                          </Button>
                        </Link>

                        <Tooltip title={copiedKey === group.key ? "Copied!" : "Copy Direct Link"}>
                          <IconButton
                            size="medium"
                            onClick={() => handleCopyLink(group)}
                            sx={{
                              borderRadius: "10px",
                              border: "1px solid #E2E8F0",
                              bgcolor: "#F8FAFC",
                              color: copiedKey === group.key ? "#10B981" : "#64748B",
                              "&:hover": {
                                bgcolor: "#F1F5F9",
                                borderColor: "#CBD5E1",
                              },
                            }}
                          >
                            {copiedKey === group.key ? <Check size={18} /> : <Copy size={18} />}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Deactivate on this device">
                          <IconButton
                            size="medium"
                            onClick={() => deactivateModule(group)}
                            sx={{
                              borderRadius: "10px",
                              border: "1px solid #FEE2E2",
                              bgcolor: "#FEF2F2",
                              color: "#EF4444",
                              "&:hover": {
                                bgcolor: "#FEE2E2",
                                borderColor: "#FCA5A5",
                              },
                            }}
                          >
                            <PowerOff size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Activation OTP Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: 1.5,
            maxWidth: "440px",
            width: "100%",
            boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, textAlign: "center" }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "16px",
              bgcolor: "rgba(37, 99, 235, 0.1)",
              color: "#2563EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
            }}
          >
            <ShieldCheck size={28} />
          </Box>
          <Typography variant="h6" fontWeight={800} color="#0F172A">
            Activate {currentGroup?.title}
          </Typography>
          <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>
            Enter the 6-digit OTP sent to the event administrator to unlock this module.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          <Box sx={{ my: 1, display: "flex", justifyContent: "center" }}>
            <OtpInput onComplete={onOtpEntered} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              flex: 1,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={verifying}
            onClick={onSubmitOtp}
            variant="contained"
            sx={{
              flex: 1,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#2563EB",
            }}
          >
            Verify & Unlock
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </OnsiteReuseContainer>
  );
};

export default SharableOnsite;
